import React, { useState } from 'react';
import { PenTool, Settings, Phone, Code } from 'lucide-react';

/**
 * Simplified visual of ScriptManager: script/prompt editor + agent settings + right rail.
 * Interactive: clicking a rail step highlights that part of the flow.
 */
export default function TutorialScriptBuilderMockup() {
  const [rail, setRail] = useState('edit');

  return (
    <div className="mt-5" aria-hidden>
      <p className="text-sm font-semibold text-[#459EA3] uppercase tracking-wide mb-2">
        Agents editor layout
      </p>
      <p className="text-sm text-gray-500 mb-4 max-w-4xl">
        Type your prompt and script in the main area. Open <strong className="text-gray-700">Step 2 · Agent Setting</strong> on the
        right to configure name, voice, and model. Use the rail to switch Build → Settings → Test → Deploy.
      </p>

      <div className="rounded-2xl border border-gray-200 bg-[#ececec] p-2 sm:p-3 shadow-inner">
        <div className="flex rounded-xl overflow-hidden border border-gray-300/90 shadow-sm min-h-[min(52vh,400px)] sm:min-h-[min(56vh,480px)] md:min-h-[min(58vh,540px)] max-h-[min(62vh,640px)]">
          {/* Main: script / prompt */}
          <div
            className={`flex-1 min-w-0 flex flex-col bg-white transition-[box-shadow] ${
              rail === 'edit' ? 'ring-2 ring-[#459EA3]/45 ring-inset z-[1]' : ''
            }`}
          >
            <div className="shrink-0 px-3 py-2 bg-gray-100 border-b border-gray-200/80 flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <span className="text-gray-400">←</span>
              <span className="truncate font-medium text-gray-700">My outreach script</span>
            </div>
            <div className="flex-1 min-h-0 flex flex-col p-3 sm:p-4 relative">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-[#459EA3] mb-2">Script / prompt</span>
              <div className="flex-1 min-h-[100px] rounded-lg border border-dashed border-[#459EA3]/50 bg-[#f8fffe] p-3 font-mono text-xs sm:text-sm leading-relaxed text-gray-600 overflow-hidden">
                You are a helpful voice assistant…
                <br />
                <br />
                <span className="text-gray-400">← Type your full script and instructions here.</span>
              </div>
            </div>
            <div className="shrink-0 px-3 py-2 border-t border-gray-100 flex gap-2 items-center">
              <span className="inline-flex rounded-lg bg-[#459EA3] text-white px-3 py-1 text-xs font-semibold">Save</span>
              <span className="text-xs text-gray-400">Create script</span>
            </div>
          </div>

          {/* Pullout: Agent settings (same place as real app when Step 2 is selected) */}
          <div
            className={`hidden sm:flex w-[34%] max-w-[180px] md:max-w-[220px] lg:max-w-[260px] flex-col border-l border-gray-200 bg-gray-100 transition-[box-shadow] ${
              rail === 'settings' ? 'ring-2 ring-[#459EA3]/45 ring-inset z-[1]' : ''
            }`}
          >
            <div className="shrink-0 px-3 py-2 border-b border-gray-200/80">
              <span className="text-xs sm:text-sm font-semibold text-gray-900">Agent settings</span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-gray-500 mb-1">Script name</label>
                <div className="h-7 sm:h-8 rounded-lg border border-gray-300 bg-white shadow-sm" />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-gray-500 mb-1">AI name</label>
                <div className="h-7 sm:h-8 rounded-lg border border-gray-300 bg-white shadow-sm" />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-gray-500 mb-1">Tone</label>
                <div className="h-7 sm:h-8 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 shadow-sm" />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-gray-500 mb-1">Voice / Model</label>
                <div className="h-7 sm:h-8 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 shadow-sm" />
              </div>
              <p className="text-[10px] sm:text-xs text-gray-400 leading-snug pt-2 border-t border-gray-200/80">
                Save settings when you change options here.
              </p>
            </div>
          </div>

          {/* Right rail — same labels as ScriptManager */}
          <div className="w-14 sm:w-16 md:w-[4.5rem] shrink-0 flex flex-col bg-[#e5e7eb] border-l border-gray-300 py-1.5 gap-1">
            <RailBtn
              active={rail === 'edit'}
              onClick={() => setRail('edit')}
              step="1"
              label="Build"
              sub="Agent"
              Icon={PenTool}
            />
            <RailBtn
              active={rail === 'settings'}
              onClick={() => setRail('settings')}
              step="2"
              label="Agent"
              sub="Setting"
              Icon={Settings}
            />
            <RailBtn
              active={rail === 'try'}
              onClick={() => setRail('try')}
              step="3"
              label="Test"
              sub="Agent"
              Icon={Phone}
            />
            <RailBtn
              active={rail === 'deploy'}
              onClick={() => setRail('deploy')}
              step="4"
              label="Deploy"
              sub=""
              Icon={Code}
            />
          </div>
        </div>
      </div>
      <p className="text-sm text-gray-500 mt-3">
        Tap a step on the right to see what it controls. Step 1 is your script text; Step 2 opens the configuration sidebar.
      </p>
    </div>
  );
}

function RailBtn({ active, onClick, step, label, sub, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center gap-1 py-1.5 px-0.5 rounded-lg transition-all min-h-0 text-[8px] sm:text-[9px] font-semibold leading-tight ${
        active ? 'bg-[#459EA3] text-white shadow-md' : 'text-gray-600 hover:bg-gray-200'
      }`}
    >
      <span className="opacity-80 uppercase tracking-wide">Step {step}</span>
      <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px] shrink-0 opacity-95" />
      <span className="text-center px-0.5">
        {label}
        {sub ? <span className="block font-normal opacity-90">{sub}</span> : null}
      </span>
    </button>
  );
}
